import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError, fileUrl } from "@/api";
import { Btn, Card, Field, TextArea, Spinner } from "@/components/ui-kit";
import { toast } from "sonner";
import { X, Upload, Info } from "lucide-react";
import { CATEGORY_OPTIONS } from "@/constants/taxonomy";
import { Section, FieldGroup, Pill, selectCls, LANGS, ACCEPTED } from "./PublishCampaign";
import { COMMISSION_HELP } from "@/lib/affiliate";

const CHANNELS = ["Instagram", "TikTok", "YouTube", "Blogi", "Muu"];
const MAX_CATEGORIES = 3;
const MAX_IMAGES = 3;
const URL_RX = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;

const EMPTY = {
  title: "", description: "", categories: [], website: "", products: "", shop_url: "", images: [],
  commission: "", join_mode: "", join_link: "", who_can_join: "selected", channels: [],
  special_terms: "", start_date: "", end_date: "", languages: ["Suomi"], additional_info: "", status: "open",
};

function ChoiceCard({ active, onClick, title, desc, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`w-full text-left rounded-2xl border p-5 transition-colors ${active ? "border-[#292827] bg-[#F6F8FB]" : "border-[#E6E1D9] hover:border-[#292827]"}`}>
      <div className="flex items-start gap-3">
        <span className={`mt-1 h-4 w-4 rounded-full border-2 shrink-0 ${active ? "border-[#292827] bg-[#292827]" : "border-[#B7B0A5]"}`} />
        <div><p className="text-[15px] font-semibold text-[#292827]">{title}</p><p className="mt-1 text-sm text-[#6E675C] leading-relaxed">{desc}</p></div>
      </div>
    </button>
  );
}

function ImagesField({ images, setImages }) {
  const [uploading, setUploading] = useState(false);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const valid = files.filter((f) => ACCEPTED.includes(f.type)).slice(0, MAX_IMAGES - images.length);
    if (valid.length < files.length) toast.error(`Enintään ${MAX_IMAGES} kuvaa · JPG, PNG, WEBP`);
    if (!valid.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of valid) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      setImages([...images, ...paths]);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  return (
    <div className="flex flex-wrap gap-3">
      {images.map((src, i) => (
        <div key={src} className="relative h-24 w-24 rounded-xl overflow-hidden border border-[#E6E1D9]" data-testid="aff-image">
          <img src={fileUrl(src)} alt="" className="h-full w-full object-cover" />
          <button type="button" onClick={() => setImages(images.filter((_, idx) => idx !== i))} data-testid="aff-remove-image"
            className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 flex items-center justify-center text-[#292827]"><X size={14} /></button>
        </div>
      ))}
      {images.length < MAX_IMAGES && (
        <label className="h-24 w-24 rounded-xl border border-dashed border-[#E6E1D9] flex flex-col items-center justify-center gap-1 text-[#968F85] cursor-pointer hover:border-[#292827] hover:text-[#292827] transition-colors" data-testid="aff-image-upload">
          <Upload size={18} /><span className="text-xs">{uploading ? "Ladataan..." : "Lisää"}</span>
          <input type="file" accept=".jpg,.jpeg,.png,.webp" multiple disabled={uploading} className="hidden" onChange={onFiles} />
        </label>
      )}
    </div>
  );
}

function validate(f) {
  if (!f.title.trim() || !f.description.trim()) return "Ohjelman nimi ja kuvaus ovat pakollisia";
  const n = Number(String(f.commission).replace(",", "."));
  if (!(n > 0 && n <= 100)) return "Provision on oltava yli 0 ja enintään 100 %";
  if (!f.join_mode) return "Valitse liittymistapa";
  if (f.join_mode === "external" && !URL_RX.test(f.join_link.trim())) return "Anna kelvollinen liittymislinkki (https://...)";
  if (f.website && !URL_RX.test(f.website.trim())) return "Verkkosivun osoitteen on alettava https://";
  if (f.shop_url && !URL_RX.test(f.shop_url.trim())) return "Verkkokaupan osoitteen on alettava https://";
  if (f.start_date && f.end_date && f.end_date < f.start_date) return "Päättymispäivä ei voi olla ennen alkamispäivää";
  return null;
}

export default function PublishAffiliate() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!!editId);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v, max) => setForm((f) => {
    const has = f[k].includes(v);
    if (!has && max && f[k].length >= max) { toast.error(`Voit valita enintään ${max}`); return f; }
    return { ...f, [k]: has ? f[k].filter((x) => x !== v) : [...f[k], v] };
  });

  useEffect(() => {
    if (!editId) return;
    api.get(`/campaigns/${editId}`).then((r) => {
      const c = r.data;
      if (c.kind !== "affiliate") { navigate(`/dashboard/julkaise?edit=${editId}`, { replace: true }); return; }
      setForm(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, c[k] ?? EMPTY[k]])));
      setFetching(false);
    }).catch(() => { toast.error("Ohjelmaa ei löytynyt"); navigate("/dashboard/omat-kampanjat"); });
  }, [editId, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    const err = validate(form);
    if (err) { toast.error(err); return; }
    setLoading(true);
    const payload = {
      ...form, kind: "affiliate",
      commission: Number(String(form.commission).replace(",", ".")),
      website: form.website.trim(), shop_url: form.shop_url.trim(), join_link: form.join_link.trim(),
      category: form.categories[0] || "",
    };
    try {
      if (editId) { await api.put(`/campaigns/${editId}`, payload); toast.success("Affiliate-ohjelma päivitetty!"); }
      else { await api.post("/campaigns", payload); toast.success("Affiliate-ohjelma julkaistu!"); }
      navigate("/dashboard/omat-kampanjat");
    } catch (er) { toast.error(formatError(er.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (fetching) return <Spinner />;

  return (
    <div data-testid="publish-affiliate-page" className="max-w-3xl">
      <h3 className="text-3xl font-semibold tracking-display text-[#292827] mb-6">{editId ? "Muokkaa affiliate-ohjelmaa" : "Julkaise affiliate-ohjelma"}</h3>
      <Card className="p-8 md:p-10">
        <form onSubmit={submit} noValidate>
          <Section title="Ohjelman tiedot" desc="Kerro sisällöntuottajille, mistä ohjelmassa on kyse." first>
            <FieldGroup label="Ohjelman nimi *"><Field value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="aff-title" /></FieldGroup>
            <FieldGroup label="Kuvaus *"><TextArea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="aff-description" /></FieldGroup>
            <FieldGroup label={`Kategoria (enintään ${MAX_CATEGORIES})`}>
              <div className="flex flex-wrap gap-2">
                {CATEGORY_OPTIONS.map((c) => <Pill key={c} active={form.categories.includes(c)} onClick={() => toggle("categories", c, MAX_CATEGORIES)} testid={`aff-category-${c}`}>{c}</Pill>)}
              </div>
            </FieldGroup>
            <FieldGroup label="Yrityksen verkkosivu"><Field type="url" placeholder="https://yritys.fi" value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="aff-website" /></FieldGroup>
          </Section>

          <Section title="Tuotteet ja verkkokauppa" desc="Mitä tuotteita sisällöntuottajat markkinoivat.">
            <FieldGroup label="Tuotteen tai tuotteiden nimet"><TextArea className="!min-h-[90px]" value={form.products} onChange={(e) => set("products", e.target.value)} data-testid="aff-products" /></FieldGroup>
            <FieldGroup label="Verkkokaupan URL-osoite"><Field type="url" placeholder="https://kauppa.fi" value={form.shop_url} onChange={(e) => set("shop_url", e.target.value)} data-testid="aff-shop-url" /></FieldGroup>
            <FieldGroup label={`Tuotekuvat (valinnainen) · Enintään ${MAX_IMAGES} kuvaa · JPG, PNG, WEBP`}>
              <ImagesField images={form.images} setImages={(v) => set("images", v)} />
            </FieldGroup>
          </Section>

          <Section title="Sisällöntuottajan provisio">
            <div className="max-w-xs">
              <FieldGroup label="Provisio (%) *">
                <Field inputMode="decimal" placeholder="esim. 10" value={form.commission}
                  onChange={(e) => /^\d{0,3}([.,]\d{0,2})?$/.test(e.target.value) && set("commission", e.target.value)} data-testid="aff-commission" />
              </FieldGroup>
            </div>
            <p className="flex gap-2 text-xs text-[#6E675C] leading-relaxed" data-testid="aff-commission-help"><Info size={14} className="shrink-0 mt-0.5" /> {COMMISSION_HELP}</p>
          </Section>

          <Section title="Liittymistapa *" desc="Valitse, miten sisällöntuottajat liittyvät ohjelmaan.">
            <ChoiceCard testid="aff-join-external" active={form.join_mode === "external"} onClick={() => set("join_mode", "external")}
              title="Yrityksellä on jo oma affiliate-ohjelma"
              desc="Sisällöntuottaja siirtyy liittymään yrityksen omaan järjestelmään. Creatori ei käsittele liittymistä, myyntiseurantaa tai provisiomaksuja." />
            {form.join_mode === "external" && (
              <FieldGroup label="Liittymislinkki *"><Field type="url" placeholder="https://..." value={form.join_link} onChange={(e) => set("join_link", e.target.value)} data-testid="aff-join-link" /></FieldGroup>
            )}
            <ChoiceCard testid="aff-join-creatori" active={form.join_mode === "creatori"} onClick={() => set("join_mode", "creatori")}
              title="Yrityksellä ei ole vielä omaa affiliate-ohjelmaa"
              desc="Sisällöntuottajat hakevat Creatorin kautta ja käsittelet hakemukset Hakemukset-osiossa. Myyntiseurannasta ja provisioiden maksamisesta sovitaan sisällöntuottajan kanssa." />
          </Section>

          <Section title="Liittymisehdot ja lisätiedot">
            {form.join_mode === "creatori" && (
              <FieldGroup label="Kuka voi liittyä">
                <div className="flex flex-wrap gap-2">
                  <Pill active={form.who_can_join === "all"} onClick={() => set("who_can_join", "all")} testid="aff-who-all">Kaikki voivat liittyä</Pill>
                  <Pill active={form.who_can_join === "selected"} onClick={() => set("who_can_join", "selected")} testid="aff-who-selected">Yritys valitsee hakijat</Pill>
                </div>
              </FieldGroup>
            )}
            <FieldGroup label="Sallitut kanavat">
              <div className="flex flex-wrap gap-2">{CHANNELS.map((c) => <Pill key={c} active={form.channels.includes(c)} onClick={() => toggle("channels", c)} testid={`aff-channel-${c}`}>{c}</Pill>)}</div>
            </FieldGroup>
            <FieldGroup label="Erityisehdot"><TextArea className="!min-h-[90px]" value={form.special_terms} onChange={(e) => set("special_terms", e.target.value)} data-testid="aff-terms" /></FieldGroup>
            <div className="grid sm:grid-cols-2 gap-5">
              <FieldGroup label="Ohjelman alkamispäivä"><Field type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="aff-start" /></FieldGroup>
              <FieldGroup label="Päättymispäivä (valinnainen)"><Field type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="aff-end" /></FieldGroup>
            </div>
            <FieldGroup label="Kielet">
              <div className="flex flex-wrap gap-2">{LANGS.map((l) => <Pill key={l} active={form.languages.includes(l)} onClick={() => toggle("languages", l)} testid={`aff-lang-${l}`}>{l}</Pill>)}</div>
            </FieldGroup>
            <FieldGroup label="Lisätiedot"><TextArea className="!min-h-[90px]" value={form.additional_info} onChange={(e) => set("additional_info", e.target.value)} data-testid="aff-info" /></FieldGroup>
            <div className="max-w-xs">
              <FieldGroup label="Tila">
                <select className={selectCls} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="aff-status">
                  <option value="open">Avoin</option><option value="draft">Luonnos</option><option value="closed">Suljettu</option>
                </select>
              </FieldGroup>
            </div>
          </Section>

          <div className="pt-8 mt-8 border-t border-[#E6E1D9]">
            <Btn type="submit" variant="dark" disabled={loading} className="w-full !py-4 !text-[16px] !rounded-2xl" data-testid="aff-submit">
              {loading ? "Tallennetaan..." : editId ? "Tallenna muutokset" : "Julkaise affiliate-ohjelma"}
            </Btn>
          </div>
        </form>
      </Card>
    </div>
  );
}
